<script setup lang="ts">
/*
 * SearchableSelectList — плоский список с поиском и подсветкой совпадений.
 * Стили поиска/highlight как в UniversalTreeSelect / FilterTreeSelect.
 * Поддерживает как клиентскую фильтрацию, так и серверный поиск с пагинацией (infinite scroll).
 */

import { refDebounced } from '@vueuse/core'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

export interface ISearchableSelectItem {
  id: string | number
  label: string
  subtitle?: string
  raw?: any
  [key: string]: any
}

interface Props {
  items: ISearchableSelectItem[]
  placeholder?: string
  maxHeight?: number | string
  serverSearch?: boolean
  loading?: boolean
  hasMore?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Поиск...',
  maxHeight: 300,
  serverSearch: false,
  loading: false,
  hasMore: false,
})

const emit = defineEmits<{
  (e: 'select', item: ISearchableSelectItem): void
  (e: 'search', query: string): void
  (e: 'load-more'): void
}>()

const search = ref('')
const searchDebounced = refDebounced(search, 300)

const observerElement = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null

watch(searchDebounced, val => {
  if (props.serverSearch) {
    emit('search', val.trim())
  }
})

const highlightMatch = (label: string, searchTerm: string) => {
  if (!searchTerm) return label
  const safe = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${safe})`, 'gi')
  return (label ?? '').replace(re, '<mark class="search-highlight">$1</mark>')
}

const filteredItems = computed(() => {
  if (props.serverSearch) {
    return props.items
  }

  const term = searchDebounced.value.trim().toLowerCase()
  if (!term) return props.items

  return props.items.filter(item =>
    item.label.toLowerCase().includes(term),
  )
})

const onSelect = (item: ISearchableSelectItem) => {
  emit('select', item)
  search.value = ''
  if (props.serverSearch) {
    emit('search', '')
  }
}

const resetSearch = () => {
  search.value = ''
  if (props.serverSearch) {
    emit('search', '')
  }
}

function setupObserver() {
  if (!props.serverSearch) return
  if (observer) observer.disconnect()

  observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting && props.hasMore && !props.loading) {
      emit('load-more')
    }
  }, { threshold: 0.1 })

  if (observerElement.value) {
    observer.observe(observerElement.value)
  }
}

onMounted(() => {
  setupObserver()
})

watch(
  () => [props.serverSearch, props.hasMore, props.loading],
  () => {
    if (props.serverSearch && observerElement.value && !observer) {
      setupObserver()
    }
  },
)

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<template>
  <VCard class="searchable-select-list d-flex flex-column" elevation="8">
    <VSheet class="pa-2 flex-grow-0">
      <VTextField
        v-model="search"
        :placeholder="placeholder"
        variant="outlined"
        rounded="pill"
        class="search-field"
        :append-inner-icon="search ? undefined : 'ri-search-line'"
        hide-details
        clearable
        autofocus
        autocomplete="off"
        @click:clear="resetSearch"
      />
    </VSheet>

    <VDivider />

    <VSheet
      class="tree-view-content flex-grow-1"
      :style="{ maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }"
    >
      <VList class="py-0">
        <VListItem
          v-for="item in filteredItems"
          :key="item.id"
          class="cursor-pointer"
          @click="onSelect(item)"
        >
          <slot name="item" :item="item" :highlight-match="(text: string) => highlightMatch(text, searchDebounced)">
            <!-- eslint-disable-next-line vue/no-v-html -- подсветка search-highlight как в UniversalTreeSelect -->
            <VListItemTitle
              class="text-body-1 text-high-emphasis text-wrap"
              v-html="highlightMatch(item.label, searchDebounced)"
            />
            <VListItemSubtitle
              v-if="item.subtitle"
              class="text-caption text-medium-emphasis text-truncate"
            >
              {{ item.subtitle }}
            </VListItemSubtitle>
          </slot>
          <template v-if="$slots.append" #append>
            <slot name="append" :item="item" />
          </template>
        </VListItem>

        <!-- Индикатор загрузки при серверном поиске / догрузке -->
        <div
          v-if="loading"
          class="d-flex align-center justify-center pa-3"
        >
          <VProgressCircular
            indeterminate
            size="24"
            width="2"
            color="primary"
          />
        </div>

        <!-- Триггер-элемент для IntersectionObserver при infinite scroll -->
        <div
          v-if="serverSearch && hasMore"
          ref="observerElement"
          style="height: 10px; width: 100%;"
        />

        <div
          v-if="!filteredItems.length && !loading"
          class="d-flex align-center justify-center pa-4 text-medium-emphasis text-body-2"
        >
          Ничего не найдено
        </div>
      </VList>
    </VSheet>
  </VCard>
</template>

<style scoped lang="scss">
.searchable-select-list {
  .text-wrap {
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
  }
}

.tree-view-content {
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 0.5rem;
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: rgb(var(--v-theme-secondary), var(--v-activated-opacity));
    border-radius: 0.5rem;
  }

  &::-webkit-scrollbar-thumb {
    background: rgb(var(--v-theme-secondary), 0.32) !important;
    border-radius: 0.5rem;
    inline-size: 0.25rem;
    min-height: 6.25rem !important;

    &:hover {
      inline-size: 0.375rem;
      background: rgb(var(--v-theme-primary)) !important;
    }
  }
}

:deep(.search-highlight) {
  background-color: rgb(var(--v-theme-primary-500), var(--v-activated-opacity));
  padding: 2px 0;
  border-radius: 2px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

:deep(.v-theme--dark .search-highlight) {
  background-color: rgba(var(--v-theme-primary-500), 0.38);
  padding: 2px 0;
  border-radius: 2px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}
</style>

